/**
 * In-Memory LRU Cache for Media Files
 * 
 * Fast, session-scoped caching layer that stores recently accessed media
 * in memory for instant retrieval. Uses Least Recently Used (LRU) eviction
 * policy to manage memory efficiently.
 */

export interface CacheEntry {
    data: string;           // Base64 data URL
    size: number;           // Size in bytes
    contentType: string;    // MIME type
    timestamp: number;      // When entry was added/accessed
    accessCount: number;    // Number of times accessed
}

export interface MemoryCacheConfig {
    maxSize: number;        // Maximum cache size in bytes
    maxEntries: number;     // Maximum number of entries
}

const DEFAULT_CONFIG: MemoryCacheConfig = {
    maxSize: 50 * 1024 * 1024,  // 50MB default
    maxEntries: 100,
};

class MemoryCache {
    private cache: Map<string, CacheEntry> = new Map();
    private config: MemoryCacheConfig;
    private currentSize: number = 0;

    constructor(config: Partial<MemoryCacheConfig> = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config };
        console.log('[MemoryCache] Initialized with config:', this.config);
    }

    /**
     * Get item from cache
     */
    get(key: string): string | null {
        const entry = this.cache.get(key);
        if (!entry) {
            console.log(`[MemoryCache] MISS: ${key}`);
            return null;
        }

        // Update access timestamp and count (LRU tracking)
        entry.timestamp = Date.now();
        entry.accessCount++;

        // Move to end of Map (most recently used)
        this.cache.delete(key);
        this.cache.set(key, entry);

        console.log(`[MemoryCache] HIT: ${key} (${(entry.size / 1024).toFixed(2)} KB, accessed ${entry.accessCount}x)`);
        return entry.data;
    }

    /**
     * Set item in cache
     */
    set(key: string, data: string, contentType: string = 'application/octet-stream'): void {
        // Calculate size (approximate for base64)
        const sizeBytes = this.calculateSize(data);

        // Check if single item exceeds max size
        if (sizeBytes > this.config.maxSize) {
            console.warn(`[MemoryCache] Item too large to cache: ${key} (${(sizeBytes / 1024 / 1024).toFixed(2)} MB)`);
            return;
        }

        // Remove existing entry if present
        if (this.cache.has(key)) {
            const existing = this.cache.get(key)!;
            this.currentSize -= existing.size;
            this.cache.delete(key);
        }

        // Evict entries until we have space
        this.evictIfNeeded(sizeBytes);

        const entry: CacheEntry = {
            data,
            size: sizeBytes,
            contentType,
            timestamp: Date.now(),
            accessCount: 0,
        };

        this.cache.set(key, entry);
        this.currentSize += sizeBytes;

        console.log(`[MemoryCache] SET: ${key} (${(sizeBytes / 1024).toFixed(2)} KB, total: ${(this.currentSize / 1024 / 1024).toFixed(2)} MB)`);
    }

    /**
     * Check if key exists in cache
     */
    has(key: string): boolean {
        return this.cache.has(key);
    }

    /**
     * Remove item from cache
     */
    delete(key: string): boolean {
        const entry = this.cache.get(key);
        if (entry) {
            this.currentSize -= entry.size;
            this.cache.delete(key);
            console.log(`[MemoryCache] DELETE: ${key}`);
            return true;
        }
        return false;
    }

    /**
     * Clear entire cache
     */
    clear(): void {
        this.cache.clear();
        this.currentSize = 0;
        console.log('[MemoryCache] CLEARED');
    }

    /**
     * Get cache statistics
     */
    getStats(): {
        entries: number;
        size: number;
        maxSize: number;
        utilizationPercent: number;
    } {
        return {
            entries: this.cache.size,
            size: this.currentSize,
            maxSize: this.config.maxSize,
            utilizationPercent: (this.currentSize / this.config.maxSize) * 100,
        };
    }

    /**
     * Calculate size of base64 data
     */
    private calculateSize(data: string): number {
        // Remove data URL prefix if present
        const base64Part = data.replace(/^data:[^;]+;base64,/, '');
        // Base64 represents 6 bits per character, so actual size is ~3/4 of string length
        return Math.floor((base64Part.length * 3) / 4);
    }

    /**
     * Evict least recently used entries until we have space
     */
    private evictIfNeeded(requiredSpace: number): void {
        // Check entry count limit
        while (this.cache.size >= this.config.maxEntries) {
            this.evictLRU();
        }

        // Check size limit
        while (this.currentSize + requiredSpace > this.config.maxSize && this.cache.size > 0) {
            this.evictLRU();
        }
    }

    /**
     * Evict the least recently used entry
     */
    private evictLRU(): void {
        // Map maintains insertion order, first entry is LRU
        const firstKey = this.cache.keys().next().value;
        if (firstKey) {
            const entry = this.cache.get(firstKey)!;
            this.currentSize -= entry.size;
            this.cache.delete(firstKey);
            console.log(`[MemoryCache] EVICT (LRU): ${firstKey} (${(entry.size / 1024).toFixed(2)} KB)`);
        }
    }

    /**
     * Get all keys in cache (for debugging)
     */
    keys(): string[] {
        return Array.from(this.cache.keys());
    }
}

// Singleton instance
export const memoryCache = new MemoryCache();

// Export class for testing/custom instances
export { MemoryCache };
